export const THEME_COOKIE_KEY = 'theme-preference';
export const DEFAULT_THEME_STORAGE_KEY = 'theme';

const THEME_COOKIE_EXPIRES_DAYS = 365;
const THEME_VALUES = ['light', 'dark'] as const;

export type ThemePreference = (typeof THEME_VALUES)[number];

export function isThemePreference(value: string | undefined | null): value is ThemePreference {
  return value === 'light' || value === 'dark';
}

export function getThemePreferenceCookie(): ThemePreference | undefined {
  if (typeof document === 'undefined') return undefined;

  const cookieTheme = document.cookie
    .split('; ')
    .find((cookie) => cookie.indexOf(`${THEME_COOKIE_KEY}=`) === 0);

  if (!cookieTheme) return undefined;

  const themePreference = decodeURIComponent(cookieTheme.split('=').slice(1).join('='));

  if (isThemePreference(themePreference)) return themePreference;

  return undefined;
}

export function getThemeCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const frontendHosts = [
    process.env.NEXT_PUBLIC_FE_URL_AUTH,
    process.env.NEXT_PUBLIC_FE_URL_WALLET,
    process.env.NEXT_PUBLIC_FE_URL_EOR,
    process.env.NEXT_PUBLIC_FE_URL_ADMIN,
  ]
    .map((url) => {
      if (!url) return undefined;

      try {
        return new URL(url).hostname;
      } catch {
        return undefined;
      }
    })
    .filter((hostname): hostname is string => !!hostname && !isLocalhost(hostname));

  const sharedDomain = getSharedCookieDomain(frontendHosts);
  if (sharedDomain) return sharedDomain;

  const currentHostname = window.location.hostname;
  if (isLocalhost(currentHostname)) return undefined;

  const parts = currentHostname.split('.');
  if (parts.length >= 2) return `.${parts.slice(-2).join('.')}`;

  return undefined;
}

export function setThemePreferenceCookie(themePreference: ThemePreference): void {
  if (typeof document === 'undefined') return;

  const domain = getThemeCookieDomain();
  const expires = new Date();

  expires.setDate(expires.getDate() + THEME_COOKIE_EXPIRES_DAYS);

  const cookieParts = [
    `${encodeURIComponent(THEME_COOKIE_KEY)}=${encodeURIComponent(themePreference)}`,
    `expires=${expires.toUTCString()}`,
    'path=/',
    'SameSite=Lax',
  ];

  if (domain) cookieParts.push(`domain=${domain}`);
  if (typeof window !== 'undefined' && window.location.protocol === 'https:')
    cookieParts.push('Secure');

  document.cookie = cookieParts.join('; ');
}

function isLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname.includes('127.0.0.1');
}

function getSharedCookieDomain(hostnames: string[]): string | undefined {
  if (hostnames.length < 2) return undefined;

  const firstHostname = hostnames[0];
  if (!firstHostname) return undefined;

  const otherHostnames = hostnames.slice(1);
  const firstParts = firstHostname.split('.').reverse();
  const sharedParts = firstParts.filter((part, index) =>
    otherHostnames.every((hostname) => hostname.split('.').reverse()[index] === part)
  );

  if (sharedParts.length < 2) return undefined;

  return `.${sharedParts.reverse().join('.')}`;
}
